/*
 * Cross-tenant monetisation surfaces — Transactions / Invoices / Usage
 * & Quotas. Single component per `view` prop. Transactions + invoices
 * read real provider payment_records via /api/admin/payments/records;
 * usage aggregates per-workspace plan + quota state.
 *
 * v1.3.0: transactions/invoices switched from "subscription-state
 * snapshot" to real per-payment rows. Empty when no provider has been
 * configured + no webhook fired yet (workspace owners still see
 * subscription state on /app/admin/workspace/usage).
 */

import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { Link } from "react-router-dom";
import { api, ApiError } from "../lib/api.js";
import { SkeletonStat, SkeletonTable } from "../components/Skeleton.js";

interface WorkspaceLite { id: string; workspaceName?: string; name?: string; slug?: string; status?: string }
interface PlanDetail {
  workspace: { id: string; name?: string; slug?: string; status?: string };
  subscription: { status?: string; planId?: string } | null;
  plan: { id?: string; name?: string; priceMonthlyUsd?: number; seatLimit?: number; dhis2InstanceLimit?: number } | null;
  subscriptionActive: boolean;
  usage: { seats: number; datasets: number; dhis2Instances: number; publicPortals: number };
}

type PaymentRecord = Awaited<ReturnType<typeof api.listAllPaymentRecords>>["records"][number];

export interface MonetisationOverviewProps {
  view: "transactions" | "invoices" | "usage";
}

export default function MonetisationOverview({ view }: MonetisationOverviewProps) {
  const [details, setDetails] = useState<PlanDetail[] | null>(null);
  const [payments, setPayments] = useState<PaymentRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.workspace()
      .then(async (r) => {
        const ws = r.workspaces as WorkspaceLite[];
        const detailPromises = ws.map((w) => api.workspacePlanDetail(w.id).catch(() => null));
        const detailArr = ((await Promise.all(detailPromises)).filter((d) => d !== null) as unknown) as PlanDetail[];
        if (!cancelled) setDetails(detailArr);
      })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : String(e)); });
    if (view === "transactions" || view === "invoices") {
      api.listAllPaymentRecords()
        .then((r) => { if (!cancelled) setPayments(r.records); })
        .catch(() => { if (!cancelled) setPayments([]); });
    }
    return () => { cancelled = true; };
  }, [view]);

  const summary = useMemo(() => {
    const out = { totalMrr: 0, activeSubs: 0, atRisk: 0 };
    for (const d of details ?? []) {
      const price = d.plan?.priceMonthlyUsd ?? 0;
      if (d.subscriptionActive) {
        out.totalMrr += price;
        out.activeSubs += 1;
      }
      if (d.subscription?.status === "past-due") out.atRisk += 1;
    }
    return out;
  }, [details]);

  const title = view === "transactions" ? "Transactions" : view === "invoices" ? "Invoices" : "Usage & quotas";
  const blurb =
    view === "transactions" ? "Per-tenant payment activity. Each row is a real provider payment recorded via the payment-webhook endpoint."
    : view === "invoices" ? "Completed payments across every tenant — the closest the platform stores to a per-tenant invoice today. Workspace owners see their own history under the workspace billing page."
    : "Usage vs plan quotas across every workspace.";

  const visiblePayments: PaymentRecord[] = view === "transactions"
    ? (payments ?? [])
    : view === "invoices"
      ? (payments ?? []).filter((p) => p.status === "completed" || p.status === "refunded")
      : [];

  function fmtAmount(p: PaymentRecord): string {
    if (p.amount == null || Number.isNaN(p.amount)) return "—";
    const cur = (p.currency ?? "USD").toUpperCase();
    return `${cur} ${Number(p.amount).toFixed(2)}`;
  }
  function fmtTime(iso?: string | null): string {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso ?? "—"; }
  }
  function statusPill(s: string): JSX.Element {
    const cls =
      s === "completed" ? "pill pill-ok"
      : s === "refunded" ? "pill pill-info"
      : s === "failed" ? "pill pill-err"
      : "pill pill-muted";
    return <span className={cls}>{s}</span>;
  }

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>{title}</h1>
          <p className="muted">{blurb}</p>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}

      {(view === "transactions" || view === "invoices") && details && (
        <section className="stat-grid">
          <div className="stat"><div className="stat-label">Active subscriptions</div><div className="stat-value">{summary.activeSubs}</div></div>
          <div className="stat"><div className="stat-label">Estimated MRR (USD)</div><div className="stat-value">${summary.totalMrr.toLocaleString()}</div></div>
          <div className="stat"><div className="stat-label">At risk</div><div className="stat-value" style={{ color: summary.atRisk > 0 ? "#b91c1c" : undefined }}>{summary.atRisk}</div></div>
        </section>
      )}

      {!details && (
        <>
          {(view === "transactions" || view === "invoices") && (
            <section className="stat-grid"><SkeletonStat /><SkeletonStat /><SkeletonStat /></section>
          )}
          <SkeletonTable rows={5} columns={view === "usage" ? 7 : 5} />
        </>
      )}
      {details && details.length === 0 && view === "usage" && (
        <div className="empty-state-card">
          <h2 style={{ marginTop: 0 }}>No workspaces yet</h2>
          <p>Once a tenant signs up, their billing state lands here.</p>
        </div>
      )}

      {/* Transactions / Invoices: real payment_records from /api/admin/payments/records.
          Empty state when no provider has been configured + no webhook fired. */}
      {(view === "transactions" || view === "invoices") && payments !== null && (
        visiblePayments.length === 0 ? (
          <div className="empty-state-card">
            <h2 style={{ marginTop: 0 }}>
              {view === "transactions" ? "No payments recorded yet" : "No completed payments yet"}
            </h2>
            <p>
              Provider payments land here once a payment provider is configured under{" "}
              <Link to="/admin/administration/monetisation/payment-gateways">Payment gateways</Link>{" "}
              and the corresponding <code>/api/saas/billing/payment-webhook/&lt;provider&gt;</code> endpoint
              receives its first event.
            </p>
          </div>
        ) : (
          <table className="transfer-table">
            <thead>
              <tr>
                <th>{view === "transactions" ? "Initiated" : "Completed"}</th>
                <th>Workspace</th>
                <th>Plan</th>
                <th>Provider</th>
                <th>Reference</th>
                <th>Amount</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visiblePayments.map((p) => (
                <tr key={p.id}>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {fmtTime(view === "transactions" ? p.initiatedAt : (p.completedAt ?? p.initiatedAt))}
                  </td>
                  <td>
                    <code style={{ fontSize: 11 }}>{p.workspaceId}</code>
                  </td>
                  <td><code style={{ fontSize: 11 }}>{p.planId}</code></td>
                  <td>{p.providerName}</td>
                  <td className="mono" style={{ fontSize: 11 }}>{p.providerReference ?? "—"}</td>
                  <td>{fmtAmount(p)}</td>
                  <td>{statusPill(p.status)}</td>
                  <td>
                    <Link to={`/admin/workspaces/${p.workspaceId}`} className="btn btn-ghost btn-sm">Open workspace</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      {/* Usage view keeps the per-workspace plan rollup. */}
      {view === "usage" && details && details.length > 0 && (
        <table className="transfer-table">
          <thead>
            <tr>
              <th>Workspace</th>
              <th>Plan</th>
              <th>Subscription</th>
              <th style={{ textAlign: "right" }}>Seats</th>
              <th style={{ textAlign: "right" }}>Instances</th>
              <th style={{ textAlign: "right" }}>Datasets</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {details.map((d) => (
              <tr key={d.workspace.id}>
                <td>
                  <div style={{ fontWeight: 500 }}>{d.workspace.name ?? d.workspace.slug ?? d.workspace.id}</div>
                  <code style={{ fontSize: 11 }}>{d.workspace.id}</code>
                </td>
                <td>{d.plan?.name ?? "—"}</td>
                <td>
                  {d.subscriptionActive
                    ? <span className="pill pill-ok">{d.subscription?.status ?? "active"}</span>
                    : <span className="pill pill-muted">{d.subscription?.status ?? "inactive"}</span>}
                </td>
                <td style={{ textAlign: "right" }}>{d.usage.seats}{d.plan?.seatLimit ? ` / ${d.plan.seatLimit}` : ""}</td>
                <td style={{ textAlign: "right" }}>{d.usage.dhis2Instances}{d.plan?.dhis2InstanceLimit ? ` / ${d.plan.dhis2InstanceLimit}` : ""}</td>
                <td style={{ textAlign: "right" }}>{d.usage.datasets}</td>
                <td>
                  <Link to={`/admin/workspaces/${d.workspace.id}`} className="btn btn-ghost btn-sm">Open</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
